/**
 * Presentational primitives for the calibration data hub.
 *
 * Client-facing surface (CHRO/recruiter), so everything is brand-styled with
 * the local `C/DM/UI` tokens (warm paper, ink neutrals, red accent) — the same
 * system the evaluation result screens use — and the copy is plain-HR (no
 * engine/stats jargon), per the tone contract.
 */

import type { CSSProperties, ReactNode } from "react";
import { C, DM, UI } from "../results/ui-tokens";

export function fmtPct(x: number | null | undefined, digits = 0): string {
  if (typeof x !== "number" || Number.isNaN(x)) return "—";
  return `${(x * 100).toFixed(digits)}%`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

/** A white content panel. */
export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** A titled section block with an optional one-line hint. */
export function Section({
  title,
  hint,
  right,
  children,
  testId,
}: {
  title: string;
  hint?: string;
  right?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section data-testid={testId} style={{ marginBottom: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ ...DM, margin: 0, fontSize: 18, fontWeight: 600, color: C.ink }}>{title}</h2>
          {hint && (
            <p style={{ ...UI, margin: "3px 0 0", fontSize: 13, color: C.ink50, lineHeight: 1.5 }}>
              {hint}
            </p>
          )}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/** A headline metric. */
export function StatCard({
  label,
  value,
  sub,
  accent,
  testId,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "14px 16px",
        minWidth: 140,
        flex: "1 1 140px",
      }}
    >
      <div
        style={{
          ...UI,
          fontSize: 11,
          fontWeight: 600,
          color: C.ink50,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          ...DM,
          fontSize: 26,
          fontWeight: 600,
          color: accent ?? C.ink,
          lineHeight: 1.15,
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {sub && <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** A small rounded tag. */
export function Chip({
  children,
  fg,
  bg,
  title,
}: {
  children: ReactNode;
  fg?: string;
  bg?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11.5,
        fontWeight: 600,
        color: fg ?? C.ink70,
        background: bg ?? C.ink5,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** Empty / hint block inside a card. */
export function Muted({ children }: { children: ReactNode }) {
  return <div style={{ ...UI, fontSize: 13, color: C.ink50, lineHeight: 1.55 }}>{children}</div>;
}
